import { useCallback, useEffect } from "react";

import * as Api from "@modules/api";
import { createPageElement } from "@modules/content/creators/createPageElement";
import { usePageData } from "@modules/content/hooks/usePageData";
import { ContentPage } from "@modules/content/types";
import { setAlert, useAppDispatch, useAppSelector } from "@lib/stores";
import {
  clearSelectedPlayerContent,
  getContentEntityItemsById,
  getContentEntityVoiceOvers,
  getDynamicLink,
} from "@lib/stores/content";
import { useSelectedPlayerContent } from "@modules/content/hooks/useSelectedPlayerContent";
import { useGetContentProgress } from "@modules/content/hooks/useGetContentProgress";
import { useAudioPlayer } from "@modules/content/AudioPlayerContext";
import { ContentActions } from "@lib/stores/content/slices";
import { useGetDynamicLink } from "@lib/stores/content/hooks";
import { AlertType } from "@lib/stores/common/types";
import { translate } from "@modules/localizations/translate";
import { setStorage } from "@modules/storage/storageHelper";
import { SelectedPlayerContentSource } from "@lib/stores/content/types";
import { getQueryFromUrl } from "@utils/helpers/getQueryFromUrl";
import { FullBookContentDetails } from "./FullBookContentDetails";
import { FullBookContentDetailsParameters } from "@components/layouts/b2c/content/FullBookSummary";
import PageWithDynamicZoneLayout from "@components/layouts/content/PageWithDynamicZoneLayout";
import ContentComponentSelector from "@modules/content/components";
import { useLockedContents } from "@modules/content/hooks/useLockedContent";

export interface FullBookDetailsParameters {
  image: string | null;
  skillIcon: string | null;
}

export const FullBookDetails = createPageElement(
  ({ pageId, context, onPageLoading }, ref) => {
    const {
      pageData,
      isLoading,
      getParameterValues: getParameterValue,
      content,
      refresh,
    } = usePageData<FullBookContentDetailsParameters>(
      {
        pageId: pageId ?? "",
        context,
      },
      ref
    );

    const params = getParameterValue([
      "aboutTitle",
      "about",
      "authors",
      "description",
      "descriptionTitle",
      "recommendedTo",
      "recommendedToTitle",
      "readingIcon",
      "readingTitle",
      "listeningIcon",
      "listeningTitle",
      "thumbnail",
      "authorsTitle",
      "categories",
      "salesCount",
      "totalPageCount",
      "chapters",
      "relatedContent",
    ]);

    const nextItemSource = getQueryFromUrl("nextItemSource");
    const playlistId = getQueryFromUrl("playlistId");

    const dispatch = useAppDispatch();

    const user = useAppSelector((state) => state.auth.user);

    const chapters = useAppSelector(
      ({ content }) => content.visitedPlayerContent.chapters.data
    );

    const { data: dynamicLink } = useGetDynamicLink();

    const progress = useAppSelector(({ content: contentState }) =>
      contentState.progresses.find((p) => p.contentEntityId === content?.id)
    );

    useGetContentProgress(Boolean(content?.id), [
      content?.id ?? "",
      ...(chapters?.map((s) => s.id ?? "") ?? []),
    ]);

    const {
      onPlayerContentSelect,
      onPlayingChapterSelect,
      onFirstPlayingChapterSelect,
      playingChapterId,
    } = useSelectedPlayerContent();

    const { togglePlayer, changeIsAutoStart, load, unload, isPlaying } =
      useAudioPlayer();

    const onAudioSelect = useCallback(
      (id: string | undefined) => {
        unload();

        if (progress) {
          const audioUrl = chapters.find((c) => c.id === id)?.signedAudioUrl;

          if (audioUrl) {
            setTimeout(() => {
              load(audioUrl);
            }, 100);
          }
        }
      },
      [unload, progress, chapters, load]
    );

    const onListen = async (selectedChapterId?: string) => {
      clearSelectedPlayerContent();

      togglePlayer(true);
      changeIsAutoStart(!user.autoPlayDisabled);

      if (content?.id && progress) {
        onPlayerContentSelect({
          content: {
            id: content.id,
            title: content.title,
            author: content.authors?.join(", "),
            thumbnail: params.thumbnail,
            contentEntityType: content.contentEntityType,
            source: SelectedPlayerContentSource.Details,
          },
          nextItemSource: nextItemSource ? Number(nextItemSource) : undefined,
          playlistId: playlistId ? Number(playlistId) : undefined,
        });

        const chapterId =
          selectedChapterId || progress.currentItem || chapters[0].id;

        onPlayingChapterSelect(chapterId, () => {
          onAudioSelect(chapterId);
        });
        onFirstPlayingChapterSelect(chapterId);

        const voiceOversResponse = await getContentEntityVoiceOvers(
          content?.id || ""
        );

        let voiceOvers: Api.Server.ContentEntityVoiceOverOutput[] = [];

        if (voiceOversResponse.meta.requestStatus === "fulfilled")
          voiceOvers = voiceOversResponse.payload;
        const currentVoiceOver = voiceOvers.find(
          (v) => v.narratorId === chapters[0].narratorId
        );

        setStorage({
          key: "LATEST_PLAYING_CONTENT",
          data: {
            content,
            pageId,
            item: params,
            voiceOvers: {
              data: voiceOvers,
              currentVoiceOver,
            },
          },
        });
      }
    };

    useEffect(() => {
      if (content?.id) {
        getContentEntityItemsById({ contentEntityId: content.id });

        getDynamicLink(content.id);
      }
    }, [content?.id]);

    useEffect(() => {
      if (content?.id) {
        dispatch(ContentActions.getContentEntitiesProgressAsync([content.id]));
      }
    }, [playingChapterId, content?.id, dispatch]);

    useEffect(() => {
      let interval: NodeJS.Timer;

      if (isPlaying) {
        interval = setInterval(() => {
          if (content?.id) {
            dispatch(
              ContentActions.getContentEntitiesProgressAsync([content.id])
            );
          }
        }, 3000 * 60);
      }

      return () => {
        clearInterval(interval);
      };
    }, [content?.id, dispatch, isPlaying]);

    useEffect(() => {
      if (!pageData?.id || pageData.id !== pageId) {
        onPageLoading && onPageLoading(undefined, isLoading);
      } else {
        onPageLoading && onPageLoading(pageData, isLoading);
      }
    }, [pageData?.id, pageId, isLoading, onPageLoading, pageData]);

    const { isLockedContent } = useLockedContents([content]);
    const isLocked = isLockedContent?.result?.[0].isLocked;

    if (!pageData) return null;

    return (
      <PageWithDynamicZoneLayout
        items={pageData?.dynamicZone ?? []}
        onRefresh={refresh}
        context={content || undefined}
        withContext
      >
        <div className={`flex h-full w-full items-center justify-start`}>
          <div className="h-full min-w-[300px] max-w-[300px] border border-[#898A8D29]/15 bg-[#F9F9F9] px-3 py-8 dark:bg-[#102133]">
            <div className="h-full w-full overflow-hidden">
              <div className="flex h-full flex-col items-center justify-start gap-y-4">
                <ContentComponentSelector
                  identifier="COM:RELATED:CARD"
                  id={params?.relatedContent ?? ""}
                  context={content}
                  componentRef={null}
                />
                <ContentComponentSelector
                  identifier="COM:FULL:BOOK:CHAPTERS"
                  id={params?.chapters ?? ""}
                  context={content}
                  componentRef={null}
                  extraProps={{
                    isLocked,
                  }}
                  onItemPress={async (content) => {
                    if (playingChapterId) {
                      !isLocked &&
                        onPlayingChapterSelect(content.id, () => {
                          onAudioSelect(content.id);
                        });
                    } else {
                      await onListen(content.id);
                    }
                  }}
                />
              </div>
            </div>
          </div>
          <FullBookContentDetails
            content={content || undefined}
            chapters={chapters}
            params={params}
            playingChapterId={playingChapterId}
            progress={progress}
            isLocked={isLocked}
            menu={{
              onShareClick: () => {
                if (navigator && dynamicLink?.link) {
                  navigator.clipboard.writeText(dynamicLink.link);

                  setAlert({
                    show: true,
                    for: "page",
                    type: AlertType.SUCCESS,
                    messages: [
                      {
                        code: 0,
                        field: null,
                        message: translate(
                          "content-details.messages.the link has been copied successfully"
                        ),
                      },
                    ],
                  });
                }
              },
            }}
            onListen={async () => {
              await onListen();
            }}
            onListenToChapter={() => {
              togglePlayer(true);
              changeIsAutoStart(true);
            }}
          />
        </div>
      </PageWithDynamicZoneLayout>
    );
  }
);

export const FullBookDetailsPage: ContentPage = {
  identifier: "PG:FULL:BOOK:DETAILS",
  page: FullBookDetails,
};
